import React, { useMemo, useLayoutEffect, useRef } from 'react';
import P from 'prop-types';
import _ from 'lodash';
import { mm, getCssLengths, getNodes } from './utils';
import NewPage from './NewPage';
import './Page.styl';

interface Props {
  pageSize: string;
  padding: string;
  childrenNodes: any;
  settled: boolean;
  onSettled: Function;
  forceSettle: boolean;
  pageHeader: any;
  pageFooter: any;
  pageNumber: number | string;
  sectionPagesCount: number;
  reportPagesCount: number;
  sectionStartPageNumber: number;
}

function Page({
  pageSize,
  padding,
  childrenNodes,
  settled,
  forceSettle,
  onSettled,
  pageHeader,
  pageFooter,
  pageNumber,
  sectionPagesCount,
  reportPagesCount,
  sectionStartPageNumber,
}: Props) {
  const { pageWidth, pageHeight } = useMemo(() => {
    const standardPageSizes = {
      A4: '210x296',
    };
    const size = _.get(standardPageSizes, pageSize, pageSize);
    const [width, height] = size.split('x');
    return { pageWidth: width, pageHeight: height };
  }, [pageSize]);

  const pagePadding = useMemo(
    () =>
      getCssLengths(padding)
        .map(x => `${mm(x)}`)
        .join(' '),
    [padding],
  );

  const pageBody = useRef<any>(null);
  const pageContainer = useRef<any>(null);

  useLayoutEffect(() => {
    if (!settled) {
      const lastNode: any = _.last(childrenNodes);
      if (lastNode) {
        if (lastNode.type === NewPage) {
          onSettled({ undo: false, createNewPage: true, pageNumber });
          return;
        }
      }
      if (pageBody.current && pageBody.current.offsetHeight > pageContainer.current.offsetHeight) {
        if (forceSettle) {
          onSettled({ undo: false, createNewPage: true, pageNumber });
          return;
        }
        setTimeout(() => {
          onSettled({ undo: true, createNewPage: true, pageNumber });
        }, 0);
        return;
      }
      setTimeout(() => {
        onSettled({ pageNumber });
      }, 0);
    }
    // eslint-disable-next-line
  }, [childrenNodes.length]);

  const sectionProps = useMemo(
    () => ({
      pageNumber,
      sectionPagesCount,
      reportPagesCount,
      sectionStartPageNumber,
    }),
    [pageNumber, sectionPagesCount, reportPagesCount, sectionStartPageNumber],
  );

  return (
    <section
      className="mio-page"
      style={{
        width: `${mm(pageWidth)}`,
        height: `calc(${mm(pageHeight)} + 0.333mm)`,
        padding: pagePadding,
        fontSize: `${mm(3)}`,
      }}
    >
      <div className="mio-pageContainer" ref={pageContainer}>
        <div ref={pageBody}>{childrenNodes}</div>
      </div>
      <div className="mio-pageHeader">{getNodes(pageHeader, sectionProps)}</div>
      <div className="mio-pageFooter">{getNodes(pageFooter, sectionProps)}</div>
    </section>
  );
}

Page.propTypes = {
  pageSize: P.string.isRequired,
  padding: P.string,
  childrenNodes: P.any,
  settled: P.bool,
  onSettled: P.func,
  forceSettle: P.bool,
  pageHeader: P.any,
  pageFooter: P.any,
  pageNumber: P.oneOfType([P.number, P.string]),
  sectionPagesCount: P.number,
  reportPagesCount: P.number,
  sectionStartPageNumber: P.number,
};

Page.defaultProps = {
  padding: '20.8 14 19 14',
  childrenNodes: [],
  settled: true,
  onSettled: () => {},
  forceSettle: false,
  pageHeader: [],
  pageFooter: [],
  pageNumber: 1,
  sectionPagesCount: 0,
  reportPagesCount: 0,
  sectionStartPageNumber: 0,
};

export default Page;
